import React, { useState, useEffect } from "react";
import { siteInfo } from "../data/mockData";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Utensils,
  Beer,
  ShieldCheck,
  ExternalLink,
} from "lucide-react";

const tabs = [
  { id: "reserve", label: "Reserve a Table" },
  { id: "menu", label: "Menu Highlights" },
];

const TABLEAGENT_URL =
  "https://tableagent.com/iframe/willow-ridge-golf-country-club/?bgcolor=%23f5f5f5&textcolor=%23000000&buttoncolor=%23007bff";

const Order = () => {
  const [activeTab, setActiveTab] = useState("reserve");
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      {/* Branded Header */}
      <section className="bg-[#1B2A4A] pt-28 pb-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-white/10 border border-[#C9A96E]/40 flex items-center justify-center text-[#C9A96E] font-serif text-xl font-bold shrink-0">
                WR
              </div>
              <div>
                <h1 className="text-white font-serif text-2xl font-bold leading-tight">
                  Willow Ridge Pub & Grill
                </h1>
                <p className="text-[#C9A96E] text-sm font-medium tracking-wide">
                  Online Reservations
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/15 border border-emerald-400/30 rounded-full">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-300 text-sm font-medium">
                Reservations open
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-[72px] z-30">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="flex gap-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-6 py-4 text-sm font-medium transition-colors duration-300 ${
                  activeTab === tab.id
                    ? "text-[#1B2A4A]"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#C9A96E]" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Panels */}
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-8">
        {/* Reserve Panel */}
        {activeTab === "reserve" && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div style={{ minHeight: "736px" }}>
              <p style={{ fontSize: "14px", color: "#555", marginBottom: "8px", padding: "12px 16px 0" }}>
                Reservations are powered by TableAgent. After selecting your date and party size, you'll be taken to TableAgent's secure site to confirm your booking.
              </p>
              <iframe
                src="https://tableagent.com/iframe/willow-ridge-golf-country-club/"
                style={{ border: "0px none", minWidth: "375px", minHeight: "736px" }}
                width="100%"
                height="100%"
              />
            </div>
          </div>
        )}

        {/* Menu Highlights Panel */}
        {activeTab === "menu" && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <h2 className="text-2xl font-serif font-bold text-[#1B2A4A] mb-6">
                Pub & Grill
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    The Pub & Grill at Willow Ridge is more than just a restaurant
                    — it's the heart of the club. Enjoy classic pub favourites
                    alongside chef specials, all made with fresh ingredients.
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    Dine on our patio with panoramic views of the course or relax
                    inside our comfortable dining room. Great food, cold drinks,
                    and a welcoming atmosphere for everyone.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-[#f8f7f4] rounded-xl">
                      <Utensils size={22} className="text-[#C9A96E] mb-2" />
                      <div className="text-[#1B2A4A] font-semibold text-sm">Full Menu</div>
                      <div className="text-gray-500 text-xs">Starters to mains</div>
                    </div>
                    <div className="p-4 bg-[#f8f7f4] rounded-xl">
                      <Beer size={22} className="text-[#C9A96E] mb-2" />
                      <div className="text-[#1B2A4A] font-semibold text-sm">Full Bar</div>
                      <div className="text-gray-500 text-xs">Beer, wine & cocktails</div>
                    </div>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl">
                  <img
                    src="/images/dining2.jpg"
                    alt="Willow Ridge Pub & Grill"
                    className="w-full h-64 object-cover"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-8">
              <h2 className="text-2xl font-serif font-bold text-[#1B2A4A] mb-4">
                Weekly Specials
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { day: "Tuesday", deal: "$6 Pints of Bud Light" },
                  { day: "Wednesday", deal: "$12/lb Wings | $6 Pints or Cans of SOK" },
                  { day: "Thursday", deal: "$5 Pints of WR Lager | $5 Off Nachos" },
                  { day: "Friday", deal: "$28 Six Piece Perch Dinner" },
                ].map((special) => (
                  <div key={special.day} className="p-4 bg-[#f8f7f4] rounded-lg border-l-4 border-[#C9A96E]">
                    <div className="font-serif font-bold text-[#1B2A4A]">{special.day}</div>
                    <div className="text-gray-500 text-sm">{special.deal}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Order;
